import {
  CriteriaBuilder,
  DropzoneAreaField,
  TextField,
} from '~/common/components';
import { Button, Col, Container, Flex, Icon, Row, Title } from '@wyz/ui';

import { Trans } from '~/common/context';
import { useFormContext, useWatch } from 'react-hook-form';
import { ICustomerThemesForm } from '~/common/types';

type Props = {
  index?: number;
  isPreviewing: boolean;
  onTogglePreview: () => void;
};
function ThemeInput({ index, isPreviewing, onTogglePreview }: Props) {
  const methods = useFormContext<ICustomerThemesForm>();

  const watchedValues = useWatch({
    control: methods.control,
  }) as ICustomerThemesForm;

  const hasCriteria = index !== undefined;

  const watchedTheme = hasCriteria
    ? watchedValues.criteriaThemes[index]
    : watchedValues;
  return (
    <>
      <Flex justifyContent='end' className='mb-3'>
        <Button
          id={hasCriteria ? `preview-theme-${index}` : 'preview-theme'}
          variant='secondary'
          type='button'
          onClick={onTogglePreview}
        >
          <Icon icon={isPreviewing ? 'eye-off' : 'eye'} />
          <Trans
            i18nKey={
              isPreviewing
                ? 'app.theme.button.stop_preview'
                : 'app.theme.button.preview'
            }
          />
        </Button>
      </Flex>
      {hasCriteria && (
        <CriteriaBuilder name={`criteriaThemes.${index}.criteria`} />
      )}
      <Row>
        <Col sm={2}>
          <TextField
            name={
              hasCriteria
                ? `criteriaThemes.${index}.menuBackgroundColor`
                : 'menuBackgroundColor'
            }
            label={<Trans i18nKey='app.theme.colors.menu.background' />}
            type='color'
          />
        </Col>
        <Col sm={2}>
          <TextField
            name={
              hasCriteria
                ? `criteriaThemes.${index}.primaryColor`
                : 'primaryColor'
            }
            label={<Trans i18nKey='app.theme.colors.primary' />}
            type='color'
          />
        </Col>
        <Col sm={2}>
          <TextField
            name={
              hasCriteria
                ? `criteriaThemes.${index}.textColorActive`
                : 'textColorActive'
            }
            label={<Trans i18nKey='app.theme.colors.text.active' />}
            type='color'
          />
        </Col>
        <Col sm={2}>
          <TextField
            name={
              hasCriteria
                ? `criteriaThemes.${index}.textColorInactive`
                : 'textColorInactive'
            }
            label={<Trans i18nKey='app.theme.colors.text.inactive' />}
            type='color'
          />
        </Col>
        <Col sm={2}>
          <TextField
            name={
              hasCriteria
                ? `criteriaThemes.${index}.subtitleColor`
                : 'subtitleColor'
            }
            label={<Trans i18nKey='app.theme.colors.text.subtitle' />}
            type='color'
          />
        </Col>
        <Col sm={2}>
          <TextField
            name={
              hasCriteria
                ? `criteriaThemes.${index}.primaryTextColor`
                : 'primaryTextColor'
            }
            label={<Trans i18nKey='app.theme.colors.text.primary' />}
            type='color'
          />
        </Col>
        <Col sm={2}>
          <TextField
            name={
              hasCriteria
                ? `criteriaThemes.${index}.platformBackgroundColor`
                : 'platformBackgroundColor'
            }
            label={<Trans i18nKey='app.theme.colors.platform.background' />}
            type='color'
          />
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          <DropzoneAreaField
            name={
              hasCriteria
                ? `criteriaThemes.${index}.platformLogo`
                : 'platformLogo'
            }
            label={<Trans i18nKey='app.theme.logos.platform' />}
            filesLimit={1}
            maxFileSize={1024 * 1024} // 1Mo
            resourceType='theme.platform_logo'
          />
        </Col>
        <Col sm={6}>
          <DropzoneAreaField
            name={
              hasCriteria
                ? `criteriaThemes.${index}.automakerLogo`
                : 'automakerLogo'
            }
            label={<Trans i18nKey='app.theme.logos.automaker' />}
            filesLimit={1}
            maxFileSize={2 * 1024 * 1024} // 2Mo
            resourceType='theme.automaker_logo'
          />
        </Col>
        <Col sm={6}>
          <DropzoneAreaField
            name={hasCriteria ? `criteriaThemes.${index}.banner` : 'banner'}
            label={<Trans i18nKey='app.theme.banner.platform' />}
            filesLimit={1}
            maxFileSize={2 * 1024 * 1024} // 2Mo
            resourceType='theme.banner.platform_logo'
          />
        </Col>
        <Col sm={6}>
          <DropzoneAreaField
            tooltips={<Trans i18nKey='app.theme.banner.email.tooltip' />}
            name={
              hasCriteria
                ? `criteriaThemes.${index}.emailBanner`
                : 'emailBanner'
            }
            label={<Trans i18nKey='app.theme.banner.email' />}
            filesLimit={1}
            maxFileSize={2 * 1024 * 1024} // 2Mo
            resourceType='theme.banner.email_banner'
          />
        </Col>
      </Row>
      <Flex justifyContent='between' className='my-4'>
        <Title heading='xl' weight='bold'>
          <Trans i18nKey='app.theme.preview' />
        </Title>
      </Flex>
      <Container
        className='border border-black border-2 mb-4 p-4'
        style={{
          backgroundColor: watchedTheme.platformBackgroundColor ?? undefined,
        }}
      >
        <Row>
          <Col>
            <h4
              className='fw-bold'
              style={{ color: watchedTheme.textColorActive ?? undefined }}
            >
              <Trans i18nKey='app.theme.colors.platform.background' />
            </h4>
          </Col>
        </Row>
        <Row className='my-4'>
          <Col xs='auto'>
            {/* side menu mock, same width as the real navbar-vertical */}
            <div
              className='border border-black h-100 p-3'
              style={{
                width: '12.625rem',
                minHeight: '14rem',
                backgroundColor: watchedTheme.menuBackgroundColor ?? undefined,
              }}
            >
              <h5
                className='pb-3 fw-bold'
                style={{ color: watchedTheme.textColorActive ?? undefined }}
              >
                <Trans i18nKey='app.theme.colors.menu.background' />
              </h5>
              <p style={{ color: watchedTheme.textColorActive ?? undefined }}>
                <Trans i18nKey='app.theme.colors.text.active' />
              </p>
              <p style={{ color: watchedTheme.textColorInactive ?? undefined }}>
                <Trans i18nKey='app.theme.colors.text.inactive' />
              </p>
              <p style={{ color: watchedTheme.textColorInactive ?? undefined }}>
                <Trans i18nKey='app.theme.colors.text.inactive' />
              </p>
            </div>
          </Col>
          <Col>
            <Container
              className='border border-black p-4'
              style={{
                backgroundColor: watchedTheme.primaryColor ?? undefined,
              }}
            >
              <h5
                className='pb-4 fw-bold'
                style={{ color: watchedTheme.textColorActive ?? undefined }}
              >
                <Trans i18nKey='app.theme.colors.primary' />
              </h5>

              <p style={{ color: watchedTheme.textColorActive ?? undefined }}>
                <Trans i18nKey='app.theme.colors.text.active' />
              </p>
              <p style={{ color: watchedTheme.textColorInactive ?? undefined }}>
                <Trans i18nKey='app.theme.colors.text.inactive' />
              </p>
            </Container>
          </Col>
          <Col>
            <Container className='border border-black p-4 bg-white'>
              <h5
                className='pb-4 fw-bold'
                style={{ color: watchedTheme.textColorActive ?? undefined }}
              >
                <Trans i18nKey='app.theme.block.white' />
              </h5>

              <p style={{ color: watchedTheme.subtitleColor ?? undefined }}>
                <Trans i18nKey='app.theme.colors.text.subtitle' />
              </p>
              <Flex alignItems='center' justifyContent='around'>
                {watchedTheme.platformLogo && (
                  <img
                    src={watchedTheme.platformLogo.resourcesFormat[0].url}
                    height={40}
                    width={40}
                  />
                )}
                {watchedTheme.automakerLogo && (
                  <img
                    src={watchedTheme.automakerLogo.resourcesFormat[0].url}
                    height={40}
                    width={40}
                  />
                )}
              </Flex>
            </Container>
          </Col>
        </Row>
      </Container>
      {watchedTheme.banner && (
        <>
          <Title heading='xl' weight='bold'>
            <Trans i18nKey='app.theme.banner.platform.preview' />
          </Title>
          <Row className='flex-center pb-2'>
            <img src={watchedTheme.banner.resourcesFormat[0].url} />
          </Row>
        </>
      )}
      {watchedTheme.emailBanner && (
        <>
          <Title heading='xl' weight='bold'>
            <Trans i18nKey='app.theme.banner.email.preview' />
          </Title>
          <Row className='flex-center'>
            <img src={watchedTheme.emailBanner.resourcesFormat[0].url} />
          </Row>
        </>
      )}
    </>
  );
}

export default ThemeInput;
